import { defineStore, storeToRefs } from "pinia";
import { routeProductModal, type ProductModalOptions, type ProductModalType } from "./productModal";
import { powerPackage, vipPackage, _vipPackageEnum, packageTimeEnum, IPowerPackageKey, IPersonalPackageKey } from "@/assets/pay";
import { getUpgradeRefundAmount, type AddOrderData, type IUpgradeRefundAmountResponse } from "@/views/power/service";
import { useUserStore } from "@/store/user";
import { useEventTracking } from "@/hooks/useEventTracking";
import { showMemberUpgradePlanModal } from "@/components/Modals/MemberUpgradePlanModal/useMemberUpgradePlanModal";
import { showMemberUpgradeSuccessModal } from "@/components/Modals/MemberUpgradeSuccessModal/useMemberUpgradeSuccessModal";
import { usePackageStore } from "@/store/package";
import { toRefs } from "vue";
import { MemberType } from "./user/memberButton";
import type { ApiMenuResp } from "@/types/platformCenter";
import { useTeamEditionStore, ModalType } from "@/store/teamEdition";
import useGroupStore from "@/store/group";
import axios from "axios";
import { openMemberPurchase } from "@/views/power/memberCard/purchaseService";
import type { MemberPurchaseInput } from "@/views/power/memberCard/purchase.types";
export { isMemberUpgradePurchase } from "@/views/power/memberCard/purchaseService";

type ModalActiveType = "member" | "groupMember" | "power" | "apiResource";
type UpgradeTargetPackageKey = "vip" | "svip" | "ultra";

/** 升级退款接口 memberType：与下单 memberType 一致 */
function resolveUpgradeRefundMemberType(packageKey: keyof typeof vipPackage) {
  if (packageKey === "svip") return "plus";
  if (packageKey === "ultra") return "ultra";
  return "pro";
}

/** 升级退款接口 duration */
function resolveUpgradeRefundDuration(
  packageKey: keyof typeof vipPackage,
  timeMode: keyof typeof packageTimeEnum
) {
  if (packageKey === "ultra" && timeMode === "subscribe") {
    return "month";
  }
  if (["halfSubscribe", "subscribe"].includes(timeMode)) {
    return "month";
  }
  if (timeMode === "year_v1") {
    return "year";
  }
  return timeMode;
}

type ISuccessModalState = {
  open: boolean;
  type: "power" | "member";

  params: {
    power: {
      key: IPowerPackageKey;
    };
    personal: {
      memberType: IPersonalPackageKey;
      timeMode: keyof typeof packageTimeEnum;
      memberOrderType: number;
    };
  };
};

export const usePayStore = defineStore("pay", {
  state: () => ({
    /** 所有会员入口共用下单互斥，避免连点/切卡同时改写确认弹窗。 */
    memberPurchasePending: false,
    /**
     * 联系客服二维码
     */
    customerServiceQrCode: `https://genai.holopix.cn/uploads/20260115/customer-service-qrcode/1768461420215-m5TCLqIs8lMqRItpq33-D.png?t=` + Date.now(),
    hasSubscribeDiscount: false, // 是否拥有首次订阅优惠
    productModalState: {
      // 开通会员、充值算力弹窗
      open: false,
      active: "member" as ModalActiveType,
      upgrade: false,
      /** 打开弹窗时指定默认选中的会员类型，不传则走原有逻辑 */
      defaultMemberType: undefined as "vip" | "svip" | "ultra" | undefined,
      /** 打开弹窗时指定默认选中的时间周期（subscribe / season / year），不传则走原有逻辑 */
      defaultTimeTab: undefined as string | undefined
    },
    /**
     * 用于标记打开窗口的来源，切换tab时，方便根据来源显示对应的tab
     */
    modalActiveType: "member" as ModalActiveType,
    successModalState: {
      open: false,
      type: "power" as "power" | "member",
      params: {
        power: {
          key: "500" as IPowerPackageKey
        },
        personal: {
          memberType: "vip" as IPersonalPackageKey,
          timeMode: "subscribe" as keyof typeof packageTimeEnum,
          memberOrderType: 1 // 1:新购 2:续费 3:升级
        }
      }
    } as ISuccessModalState,
    payModalState: {
      // 支付弹窗状态
      open: false,
      payMode: "member" as "member" | "power" | "teamPower" | "apiResource" | "teamMember", // 会员/算力/团队新开通
      payChannel: "alipay" as AddOrderData["payChannel"],
      memberPackage: "vip" as IPersonalPackageKey,
      timeMode: "subscribe" as keyof typeof packageTimeEnum,
      trialDeclaration: undefined as boolean | undefined,
      isBuyout: undefined as boolean | undefined,
      /** 买断转同周期订阅：MemberUpgradePlanModal 用订阅计划文案 */
      subscribePlan: false,
      powerPackage: "500" as keyof typeof powerPackage,
      apiResourcePackage: null as ApiMenuResp | null
    },
    upgradeMemberPackageModalState: {
      // 升级套餐
      open: false,
      timeMode: "subscribe" as keyof typeof packageTimeEnum
    },
    upgradeMemberPayModalState: {
      open: false,
      amount: 0,
      payChannel: "alipay" as AddOrderData["payChannel"],
      timeMode: "subscribe" as keyof typeof packageTimeEnum
    },
    /**
     *  ballParams
     *  view 会员订阅从哪个页面弹出
     *  button 会员订阅点击哪个按钮触发弹出
     */
    ballParams: {
      view: "", // 会员订阅从哪个页面弹出
      button: "" //会员订阅点击哪个按钮触发弹出
    },
    /** 团队版本购买算力弹窗 */
    teamPowerModalState: {
      open: false
    },
    /** 是否免费用户点击生图时，点击立即升级打开购买会员弹窗 */
    isOpenFromQueueSpeed: false,
    /**
     * 购买会员后下发的算力值，通过商品id从后端获取
     * 未获取到时使用 userStore.sources.memberSendPoints 作为兜底
     */
    memberSendPoints: undefined as number | undefined,
    /**
     * 本次购买算力发放时间，来自下单接口 getCommodityInfo
     * 与 userStore.memberPointsDdl（下次会员算力重置时间）语义不同
     */
    memberPointsDdl: undefined as string | undefined,
    /** 退款信息 */
    refundInfo: {} as IUpgradeRefundAmountResponse,
    /** 是否是升级 */
    isUpgrade: false,
    /** 升级时是否需要人工操作 */
    isUpgradeNeedManual: false,
    /** 退款信息请求的 AbortController，用于取消上一次请求避免竞态问题 */
    refundInfoAbortController: null as AbortController | null,
    // 资源包状态信息
    apiResourcePackageState: {
      open: false,
      packageInfo: null as ApiMenuResp | null,
      // 总并发数
      totalQps: 0,
      buyQps: 0,
      // 成功回调信息
      orderSucResult: null as any
    }
  }),
  actions: {
    /**
     * 从服务端获取退款信息
     * @param memberType - 会员类型
     * @param duration - 时限
     * @param orderType - 订单类型
     * @returns 退款信息
     */
    /** 按目标套餐拉取升级退款信息（memberType / duration 与 upgradeRefundAmount 接口对齐） */
    async fetchUpgradeRefundForTarget(
      packageKey: keyof typeof vipPackage,
      timeMode: keyof typeof packageTimeEnum,
      isBuyout?: boolean,
      isTrial?: boolean
    ) {
      // 套餐名称（newSubject / oldSubject）与算力展示一律以后端试算为准，不做本地拼装覆盖
      return this.fetchRefundInfo(
        resolveUpgradeRefundMemberType(packageKey),
        resolveUpgradeRefundDuration(packageKey, timeMode),
        "member",
        isBuyout,
        isTrial
      );
    },
    async fetchRefundInfo(
      memberType?: string,
      duration?: string,
      orderType: string = "member",
      isBuyout?: boolean,
      isTrial?: boolean
    ): Promise<IUpgradeRefundAmountResponse> {
      const requestId = `refund_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      try {
        // 取消上一次请求（如果存在）
        if (this.refundInfoAbortController) {
          this.refundInfoAbortController.abort();
        } else {
          console.log(`[${requestId}] ℹ️  没有待取消的请求`);
        }

        // 创建新的 AbortController
        this.refundInfoAbortController = new AbortController();

        const response = await getUpgradeRefundAmount(
          {
            memberType,
            duration,
            orderType,
            ...(isBuyout != null ? { isBuyout } : {}),
            // 仅购买尝鲜试用时由调用方传 true；月付等完整版不传
            ...(isTrial === true ? { isTrial } : {})
          },
          {
            signal: this.refundInfoAbortController.signal
          }
        );

        this.isUpgradeNeedManual = response.data.upStatus === 1;

        // 请求成功后清理 AbortController
        this.refundInfoAbortController = null;

        return response.data;
      } catch (error: any) {
        // 检查是否是取消错误（兼容多种判断方式）
        const isCanceled = axios.isCancel(error) || 
                          error?.code === 'ERR_CANCELED' || 
                          error?.message?.includes('cancel') ||
                          error?.name === 'CanceledError';
        
        if (isCanceled) {
          throw error; // 重新抛出以便调用方知道请求被取消
        }

        // 清理 AbortController
        this.refundInfoAbortController = null;
        
        throw new Error("获取退款信息失败，请稍后重试");
      }
    },
    async upgradeMemberNeedManual() {
      this.fetchRefundInfo("plus", "year", "member");
    },
    /**
     * 检查目标套餐类型是否需要人工干预
     * @param memberType - 目标会员类型 ("vip" | "svip")
     * @param timeMode - 目标时间模式
     * @returns 是否需要人工干预
     */
    async checkTargetPackageNeedManual(
      memberType: keyof typeof vipPackage,
      timeMode: keyof typeof packageTimeEnum
    ): Promise<boolean> {
      const userStore = useUserStore();
      try {
        // ultra 已是最高档位，不存在"升级"场景，无需检查人工干预
        if (memberType === "ultra") {
          return false;
        }
        // 将前端会员类型转换为后端API需要的类型
        const apiMemberType = memberType === "svip" ? "plus" : "pro";
        let isUpgrade = memberType === "svip" && userStore.highestPackage === "vip";

        const teamEditionStore = useTeamEditionStore();

        // 升级且为非团队版本才需要人工干预
        if(!isUpgrade || teamEditionStore.isTeamVersion) {
          return false;
        }
        
        // 处理 svip subscribe 特殊情况，实际按 halfSubscribe 计费
        let actualTimeMode = timeMode;
        if (memberType === "svip" && timeMode === "subscribe") {
          actualTimeMode = "halfSubscribe";
        }
        
        // 如果是包半月或连续包月，转换为对应的API参数
        const apiTimeMode = actualTimeMode === "halfSubscribe" ? "month" : actualTimeMode;
        
        // 获取退款信息，同时会更新 isUpgradeNeedManual 状态
        const refundInfo = await this.fetchRefundInfo(apiMemberType, apiTimeMode, "member");
        
        // 返回是否需要人工干预
        return refundInfo.upStatus === 1;
      } catch (error: any) {
        // 检查是否是取消错误（兼容多种判断方式）
        const isCanceled = axios.isCancel(error) || 
                          error?.code === 'ERR_CANCELED' || 
                          error?.message?.includes('cancel') ||
                          error?.name === 'CanceledError';
        
        if (isCanceled) {
          return false;
        }
        
        // 发生错误时，为安全起见返回 false，不阻止用户操作
        return false;
      }
    },
    /**
     * apiResource 订单
     */
    async usePayApiResourceModal({ packageInfo }: { packageInfo: ApiMenuResp }) {
      this.payModalState.apiResourcePackage = packageInfo;
      this.payModalState.payMode = "apiResource";
      this.payModalState.open = true;
    },
    // 会员订单
    async usePayMemberModal(input: MemberPurchaseInput): Promise<void> {
      await openMemberPurchase(this, input);
    },
    // 算力订单
    async usePayPowerModal(key: keyof typeof powerPackage) {
      const userStore = useUserStore();
      await userStore.queryUserRights();

      const target = powerPackage[key];

      if (target) {
        this.payModalState.payMode = "power";
        this.payModalState.powerPackage = key;
        this.payModalState.open = true;

        this.ballParams.button = "button_buy_package";
      }
    },
    /** 团队版本算力订单 */
    async usePayTeamPowerModal(key: keyof typeof powerPackage) {
      this.payModalState.payMode = "teamPower";
      this.payModalState.open = true;
      this.payModalState.powerPackage = key;

      // 设置埋点按钮参数
      this.ballParams.button = "button_buy_team_package";
    },
    /**
     * 团队新开通：打开统一支付凭证弹窗（下单仍走 teamEditionStore）
     * 调用前须已写入 formData（版本 + 提交席位）且 modalType=OPEN_TEAM_EDITION
     */
    openTeamMemberPayModal() {
      this.payModalState.payMode = "teamMember";
      this.payModalState.payChannel = "alipay";
      this.payModalState.open = true;
      this.ballParams.button = "member_team_open";
    },
    /**
     * 打开会员开通、算力充值弹窗
     * @param options.defaultMemberType - 个人全屏高亮目标档位，团队旧弹窗保留默认选中（vip / svip / ultra）
     * @param options.defaultTimeTab - 指定弹窗打开后默认选中的时间周期（subscribe / season / year）
     */
    useProductModal(
      type: ProductModalType,
      upgrade: boolean = false,
      options?: ProductModalOptions
    ) {
      if (routeProductModal(type, upgrade, options)) return;
      /**
       * 当前为标准版本会员，升级到专业版本会员，判断人工操作
       */
      const userStore = useUserStore();
      const { currentMemberInfo } = toRefs(userStore);
      if (currentMemberInfo.value.type === MemberType.PRO && type === "member") {
        this.upgradeMemberNeedManual();
      }

      const teamEditionStore = useTeamEditionStore();
      const groupStore = useGroupStore();
      // 团队版新增席位弹窗,且已经过期的情况，打开续费订购弹窗
      if (type === "groupMember" && teamEditionStore.isTeamVersion && groupStore.isExpired) {
        teamEditionStore.setModalType(ModalType.RENEWAL_ORDER);
      }

      /**
       * 【团队版】团队版试用版或者团队已过期，所有的续费订购或者新增席位都要走新购逻辑（条件：团队试用版 || 团队已过期）
       */
      if (type === "groupMember" && teamEditionStore.isTeamVersion && (groupStore.isTrialVersion || groupStore.isExpired)) {
        teamEditionStore.setModalType(ModalType.OPEN_TEAM_EDITION);
      }

      this.productModalState.active = type;
      this.productModalState.open = true;
      this.productModalState.upgrade = upgrade;
      this.productModalState.defaultMemberType = options?.defaultMemberType;
      this.productModalState.defaultTimeTab = options?.defaultTimeTab;
      this.modalActiveType = type;

     
    },
    // 会员升级
    async useUpgradeMemberPackage(timeMode: keyof typeof packageTimeEnum) {
      this.upgradeMemberPackageModalState.timeMode = timeMode;
      this.upgradeMemberPackageModalState.open = true;
    },
    /**
     * 打开会员升级弹窗
     */
    async openMemberUpgradePlanModal(isActive: () => boolean = () => true) {
      const { memberPackage, timeMode, isBuyout, trialDeclaration, subscribePlan } = this.payModalState;
      const userStore = useUserStore();
      const purchaseUserId = userStore.userInfo.id;
      const targetPackageKey = memberPackage as UpgradeTargetPackageKey;
      const apiMemberType = resolveUpgradeRefundMemberType(targetPackageKey);

      await showMemberUpgradePlanModal({
        timeMode,
        memberType: apiMemberType,
        targetPackageKey,
        planMode: subscribePlan ? "subscribe" : "upgrade",
        isBuyout,
        onConfirm: () => {
          if (!isActive() || !userStore.isLogin || userStore.userInfo.id !== purchaseUserId) return;
          this.usePayMemberModal({
            key: memberPackage,
            timeMode,
            source: "powerPage",
            isConfirmUpgrade: true,
            isBuyout,
            trialDeclaration,
            subscribePlan,
            isActive,
            forceUpgrade: true
          });
        },
        onCancel: () => {}
      });
    },
    async useUpgradeMemberPayModal(amount: number, timeMode: keyof typeof packageTimeEnum) {
      this.upgradeMemberPayModalState.timeMode = timeMode;
      this.upgradeMemberPayModalState.amount = amount;
      this.upgradeMemberPayModalState.open = true;
    },
    /** 打开会员升级成功弹窗 */
    async openMemberUpgradeSuccessModal({
      refundInfo,
      targetPackageKey,
      targetTimeMode,
      grantedPoints,
      isAutoRenewal = false
    }: {
      refundInfo: IUpgradeRefundAmountResponse;
      targetPackageKey?: keyof typeof vipPackage;
      targetTimeMode?: keyof typeof packageTimeEnum;
      grantedPoints?: number;
      isAutoRenewal?: boolean;
    }) {
      await showMemberUpgradeSuccessModal({
        refundInfo,
        targetPackageKey,
        targetTimeMode,
        grantedPoints,
        isAutoRenewal,
        onConfirm: () => {
          console.log("用户确认升级成功");
        },
        onClose: () => {
          console.log("用户关闭弹窗");
        }
      });
    }
  },
  persist: {
    paths: []
  }
});
